import {
  BASE_WELL_LAYER,
  BASE_WELL_RASTER_LAYER,
  vertWellCircleRadius
} from "constants/mapLayers.constants";
import type mapboxgl from "mapbox-gl";

import {
  BASE_DOT_STROKE_PX,
  BASE_WELL_GRAY,
  HOLLOW_DOT_STROKE_PAINT,
  WELL_DOT_STROKE_PX,
  hollowDotColorPaint,
  insetDotRadius,
  setWellLineWidth,
  wellLineDasharray
} from "./baseWellGlyphs";

export interface BaseWellStyle {
  readonly color: string;
  readonly opacity: number;
  readonly thickness: number;
}

const DEFAULT_OPACITY = 1;
// EVA-201: 1px lines like the old GeoServer layer.
const DEFAULT_THICKNESS = 1;
// Base thickness 1 draws the same dot as the Well layer's default thickness 2.
const DOT_THICKNESS_FACTOR = 2;
const MIN_THICKNESS = 0.5;
const MAX_THICKNESS = 10;

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const clampOrDefault = (
  value: unknown,
  min: number,
  max: number,
  fallback: number
): number =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;

const resolveColor = (value: unknown): string => {
  if (typeof value !== "string" || !HEX_COLOR.test(value)) return BASE_WELL_GRAY;
  const hex = value.toLowerCase();
  return hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
};

// Saved Views are untrusted, so every read of a stored style goes through here.
export function resolveBaseWellStyle(raw: unknown): BaseWellStyle {
  const entry = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    color: resolveColor(entry.color),
    opacity: clampOrDefault(entry.opacity, 0, 1, DEFAULT_OPACITY),
    thickness: clampOrDefault(
      entry.thickness,
      MIN_THICKNESS,
      MAX_THICKNESS,
      DEFAULT_THICKNESS
    )
  };
}

export const BASE_WELL_LINES_LAYER = `${BASE_WELL_LAYER}-lines` as const;

// Single paint definition for getMapLayers and MapboxWrapper's create/reconcile
// branches so they cannot drift.
export const BASE_WELL_PAINT = {
  [BASE_WELL_LINES_LAYER]: ({
    color,
    opacity,
    thickness
  }: BaseWellStyle): mapboxgl.LinePaint => ({
    "line-color": color,
    "line-opacity": opacity,
    "line-width": thickness,
    "line-dasharray": wellLineDasharray(thickness)
  }),
  [BASE_WELL_LAYER]: ({
    color,
    opacity,
    thickness
  }: BaseWellStyle): mapboxgl.CirclePaint => ({
    ...hollowDotColorPaint(color),
    ...HOLLOW_DOT_STROKE_PAINT,
    "circle-opacity": opacity,
    "circle-stroke-opacity": opacity,
    "circle-radius": insetDotRadius(
      vertWellCircleRadius(thickness * DOT_THICKNESS_FACTOR) as mapboxgl.Expression,
      WELL_DOT_STROKE_PX
    ),
    "circle-stroke-width": BASE_DOT_STROKE_PX
  }),
  // Colour is rendered server-side via the tile URL's `c` param.
  [BASE_WELL_RASTER_LAYER]: ({ opacity }: BaseWellStyle): mapboxgl.RasterPaint => ({
    "raster-opacity": opacity
  })
} as const;

export function applyBaseWellStyle(mapbox: mapboxgl.Map, style: BaseWellStyle): void {
  for (const [id, paintFor] of Object.entries(BASE_WELL_PAINT)) {
    if (!mapbox.getLayer(id)) continue;
    for (const [property, value] of Object.entries(paintFor(style))) {
      if (
        id === BASE_WELL_LINES_LAYER &&
        (property === "line-dasharray" || property === "line-width")
      )
        continue;
      mapbox.setPaintProperty(id, property, value);
    }
  }
  setWellLineWidth(mapbox, BASE_WELL_LINES_LAYER, style.thickness);
}
